import { makeAutoObservable } from 'mobx';

import { resetAllStore } from '../../shared/stores/resetAllStore';
import {
  InbodyIndicatorInfo,
  InbodyIndicatorsFormValues,
} from '../interfaces/inbodyIndicator';
import { getScanService } from '../services/scanService/getScanService';
import { ScanService } from '../services/scanService/types';
import {
  CUSTOMER_TYPE,
  CustomerType,
} from '../../shared/constants/customer/customerType';
import { InbodyType } from '../../shared/constants/inbody-info/InbodyType';
import { Option } from '../../general-types';
import { ScanMetadata } from '../interfaces/scanMetadata';
import { MealPlanOnScanDayInfo } from '../interfaces/mealPlanOnScanDayInfo';
import { transformToOptions } from '../../shared/utils/arrayUtils';
import { getFilteredInbodyIndicators } from '../utils/getFilteredInbodyIndicators/getFilteredInbodyIndicators';
import { getFilteredScansByInbodyType } from '../utils/getFilteredScansByInbodyType/getFilteredScansByInbodyType';
import { getDefaultScansRange } from '../utils/getDefaultScansRange/getDefaultScansRange';

/*
  Deeply connected with NgzInbodyIndicatorsTab.
  Used to save data between tabs and apply filters.
  Backend filters by inbody type, we need to filter it by scan range.

  initData needs to get all necessary data (scansMetadata to get default scan range) after 
  resetByInbodyType based on inbody type filters scansMetadata, calculates default scan range, gets and filters inbodyIndicators.
*/
class InbodyIndicatorsStore {
  private customerId: number | null = null;
  private customerType: CustomerType | null = null;
  private scanService: ScanService | null = null;
  private entityId: number | undefined = undefined;
  inbodyTypeOptions: Option[] = [];
  scansMetadata: ScanMetadata[] = [];
  availableScansMetadata: ScanMetadata[] = [];
  mealPlansOnScanDay: MealPlanOnScanDayInfo[] = [];
  /* all inbody indicators filtered by inbody type */
  inbodyIndicators: InbodyIndicatorInfo[] = [];
  filteredInbodyIndicators: InbodyIndicatorInfo[] = [];
  filterData: InbodyIndicatorsFormValues = {} as InbodyIndicatorsFormValues;
  showAllScans: boolean = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setDefaultValues() {
    this.customerId = null;
    this.customerType = null;
    this.scanService = null;
    this.entityId = undefined;
    this.inbodyTypeOptions = [];
    this.scansMetadata = [];
    this.availableScansMetadata = [];
    this.mealPlansOnScanDay = [];
    this.inbodyIndicators = [];
    this.filteredInbodyIndicators = [];
    this.filterData = {} as InbodyIndicatorsFormValues;
    this.showAllScans = false;
  }

  async initData(customerId: number, customerType: CustomerType, entityId?: number) {
    // save data between tabs
    if (this.customerId === customerId && this.customerType === customerType) {
      return;
    }
    // need to omit show data for previous customer
    this.setDefaultValues();
    this.setCustomerType(customerType);
    this.setCustomerId(customerId);
    this.setEntityId(entityId);

    await this.getInitialData();
  }

  /**
   * Reinitializes data when scans changed (get new scans or add scans manually)
   * works only when customerId is set (after NgzInbodyIndicatorsTab tab opened) else it will get correct data on initData
   */
  async reInitData() {
    if (!this.customerId) {
      return;
    }

    await this.getInitialData();
  }

  private async getInitialData() {
    await Promise.all([
      this.getInbodyTypeOptions(),
      this.getScansMetadata(),
      this.getMealPlansOnScanDay(),
    ]);

    this.resetByInbodyType('ALL');
  }

  async applyFilters(filterData: InbodyIndicatorsFormValues) {
    if (filterData.inbodyType !== this.filterData.inbodyType) {
      await this.resetByInbodyType(filterData.inbodyType);
    } else {
      this.filterByScanRange(filterData);
    }
  }

  toggleShowAllScans() {
    this.showAllScans = !this.showAllScans;
  }

  /**
   * Resets all data when inbody type changes. Recalculates scan range and fetches new indicators.
   */
  private async resetByInbodyType(inbodyType: InbodyType | 'ALL') {
    const availableScansMetadata = getFilteredScansByInbodyType(
      this.scansMetadata,
      inbodyType,
    );
    const defaultScanRange = getDefaultScansRange(availableScansMetadata);

    await this.getInbodyIndicators(inbodyType);
    const filteredInbodyIndicators = getFilteredInbodyIndicators(
      this.inbodyIndicators,
      defaultScanRange.startScanId,
      defaultScanRange.endScanId,
    );

    this.setAvailableScansMetadata(availableScansMetadata);
    this.setFilteredInbodyIndicators(filteredInbodyIndicators);
    this.setFilterData({
      ...defaultScanRange,
      inbodyType,
    });
  }

  private filterByScanRange(filterData: InbodyIndicatorsFormValues) {
    const filteredInbodyIndicators = getFilteredInbodyIndicators(
      this.inbodyIndicators,
      filterData.startScanId,
      filterData.endScanId,
    );

    this.setFilteredInbodyIndicators(filteredInbodyIndicators);
    this.setFilterData(filterData);
  }

  private async getInbodyIndicators(inbodyType: InbodyType | 'ALL') {
    if (!this.customerId || !this.scanService) {
      throw new Error('customerId or scanService is not set');
    }

    const data = await this.scanService.getInbodyIndicators(
      this.customerId,
      inbodyType,
      this.entityId,
    );

    this.setInbodyIndicators(data.inbodyIndicators);
  }

  private async getInbodyTypeOptions() {
    if (!this.customerId || !this.scanService) {
      throw new Error('customerId or scanService is not set');
    }

    const inbodyTypes = await this.scanService.getCustomerInbodyTypeOptions(
      this.customerId,
      this.entityId,
    );
    const options = transformToOptions({
      list: inbodyTypes,
      valueKey: 'userInbodyType',
      labelKey: 'label',
    });

    this.setInbodyTypeOptions(options);
  }

  private async getScansMetadata() {
    if (!this.customerId || !this.scanService) {
      throw new Error('customerId or scanService is not set');
    }
    const data = await this.scanService.getScanDates(this.customerId, this.entityId);

    this.setScansMetadata(data);
  }

  private async getMealPlansOnScanDay() {
    if (!this.customerId || !this.scanService) {
      throw new Error('customerId or scanService is not set');
    }

    if (this.customerType === CUSTOMER_TYPE.NUTRITION) {
      const data = await this.scanService.getMealPlansOnScanDay(this.customerId);

      this.setMealPlansOnScanDay(data);
    }
  }

  private setCustomerType(customerType: CustomerType) {
    this.customerType = customerType;
    this.scanService = getScanService(customerType);
  }

  private setCustomerId(customerId: number) {
    this.customerId = customerId;
  }

  private setEntityId(entityId: number | undefined) {
    this.entityId = entityId;
  }

  private setInbodyTypeOptions(inbodyTypeOptions: Option[]) {
    this.inbodyTypeOptions = inbodyTypeOptions;
  }

  private setScansMetadata(scansMetadata: ScanMetadata[]) {
    this.scansMetadata = scansMetadata;
  }

  private setAvailableScansMetadata(availableScansMetadata: ScanMetadata[]) {
    this.availableScansMetadata = availableScansMetadata;
  }

  private setMealPlansOnScanDay(mealPlansOnScanDay: MealPlanOnScanDayInfo[]) {
    this.mealPlansOnScanDay = mealPlansOnScanDay;
  }

  private setInbodyIndicators(inbodyIndicators: InbodyIndicatorInfo[]) {
    this.inbodyIndicators = inbodyIndicators;
  }

  private setFilteredInbodyIndicators(filteredInbodyIndicators: InbodyIndicatorInfo[]) {
    this.filteredInbodyIndicators = filteredInbodyIndicators;
  }

  private setFilterData(filterData: InbodyIndicatorsFormValues) {
    this.filterData = filterData;
  }
}

const inbodyIndicatorsStore = new InbodyIndicatorsStore();

resetAllStore.addResetMethod(inbodyIndicatorsStore.setDefaultValues);

export { inbodyIndicatorsStore, InbodyIndicatorsStore };
